import { useNavigate } from "react-router-dom";
import { useMedicalCertificates, type MedicalCertificateListItem } from "@/lib/queries";
import { useDeleteMedicalCertificate } from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash } from "lucide-react";
import { fmtDate } from "@emr/shared/india";
import { AiBadge, CloudBadge, SentBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";

export default function CertificatesPage() {
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("certificates.create");
  const canDelete = isAdmin || permissions.has("certificates.delete");
  const { data, isLoading } = useMedicalCertificates();
  const deleteMut = useDeleteMedicalCertificate();
  const rows = data?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Medical Certificates</h1>
        {canCreate && <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => navigate("/certificates/new")}><Plus size={16} className="mr-1" /> New Certificate</Button>}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                <TableHead>Patient</TableHead><TableHead>UHID</TableHead><TableHead>Type / Recipient</TableHead><TableHead>Date</TableHead><TableHead>Signatory</TableHead><TableHead>Status</TableHead><TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? <TableRow><TableCell colSpan={7}><Skeleton className="h-10" /></TableCell></TableRow> :
               rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted py-8">No medical certificates</TableCell></TableRow> :
               rows.map((r: MedicalCertificateListItem) => (
                <TableRow key={r.id} className="even:bg-cream-50/50 cursor-pointer" onClick={() => navigate(`/certificates/${r.id}`)}>
                  <TableCell className="font-medium">{r.patientName ?? "Whomsoever"}</TableCell>
                  <TableCell className="text-xs">{r.clinicUhid ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant={r.recipientType === "whomsoever" ? "outline" : "secondary"}>
                      {r.recipientType === "whomsoever" ? "Whomsoever" : r.recipientName ?? "Specific"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{fmtDate(r.createdAt)}</TableCell>
                  <TableCell className="text-xs">{r.signatoryName ?? "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {r.aiGenerated && <AiBadge />}
                      {r.savedToR2 && <CloudBadge />}
                      {r.sentViaPabbly && <SentBadge />}
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => navigate(`/certificates/${r.id}`)}><Pencil size={14} /></Button>
                      {canDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteMut.mutate(r.id)}><Trash size={14} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
